export interface RowItem {
  dt: string;
  dd: string;
  impact?: boolean;
}

export interface UseCaseItem {
  num: string;
  numShort: string;
  title: string;
  intro: string;
  rows: RowItem[];
  color: string;
  accent: string;
  icon: "micro" | "file" | "shield" | "chat" | "calendar" | "chip" | "chart" | "sparkle" | "network" | "check";
  metricVal: string;
  metricLabel: string;
  tag: string;
}

export interface FAQItem {
  q: string;
  a: string;
}

export interface AiWorkflowsTranslations {
  heroEyebrow: string;
  heroTitle: string;
  heroDesc: string;
  bookDemo: string;
  faqsEyebrow: string;
  faqsTitle: string;
  faqsDesc: string;
  stepperLabel: string;
  moduleLabel: string;
  aiEngineLabel: string;
  cases: UseCaseItem[];
  faqs: FAQItem[];
}

export const getAiWorkflowsData = (locale: string): AiWorkflowsTranslations => {
  const getProblem = (lang: string) => {
    if (lang === "hi") return "समस्या";
    if (lang === "ar") return "المشكلة";
    if (lang === "es") return "Problema";
    if (lang === "fr") return "Problème";
    return "Problem";
  };

  const getAi = (lang: string) => {
    if (lang === "hi") return "चिकित्सा में एआई";
    if (lang === "ar") return "الذكاء الاصطناعي في تشيكيتسا";
    if (lang === "es") return "IA en Chikitsa";
    if (lang === "fr") return "IA dans Chikitsa";
    return "AI in Chikitsa";
  };

  const getImpact = (lang: string) => {
    if (lang === "hi") return "प्रभाव";
    if (lang === "ar") return "الأثر";
    if (lang === "es") return "Impacto";
    if (lang === "fr") return "Impact";
    return "Impact";
  };

  const getModule = (lang: string) => {
    if (lang === "hi") return "मॉड्यूल";
    if (lang === "ar") return "الوحدة";
    if (lang === "es") return "Módulo";
    if (lang === "fr") return "Module";
    return "Module";
  };

  const getAiCap = (lang: string) => {
    if (lang === "hi") return "एआई क्षमता";
    if (lang === "ar") return "القدرة البرمجية";
    if (lang === "es") return "Capacidad IA";
    if (lang === "fr") return "Capacité IA";
    return "AI Capability";
  };

  // Hindi mappings
  if (locale === "hi") {
    const labels = {
      problem: getProblem("hi"),
      ai: getAi("hi"),
      impact: getImpact("hi"),
      module: getModule("hi"),
      aiCap: getAiCap("hi"),
    };
    return {
      heroEyebrow: "आपके वर्कफ़्लो में एआई",
      heroTitle: "एआई जो वास्तविक अस्पताल की समस्याओं को हल करता है।",
      heroDesc: "चिकित्सा एआई को दैनिक अस्पताल संचालन में लाता है, अपॉइंटमेंट बुकिंग और ओपीडी दस्तावेज़ीकरण से लेकर दावों, डिस्चार्ज सारांश, एनालिटिक्स और रोगी जुड़ाव तक। अस्पताल के परिणाम, न कि केवल चैटबॉट डेमो।",
      bookDemo: "डेमो बुक करें",
      faqsEyebrow: "अक्सर पूछे जाने वाले प्रश्न",
      faqsTitle: "कार्यप्रवाह में एआई पर अक्सर पूछे जाने वाले प्रश्न।",
      faqsDesc: "चिकित्सा (Chikitsa) कैसे डेटा सुरक्षा, मॉडल प्रशिक्षण और नैदानिक परिशुद्धता का प्रबंधन करता है, इस पर पाँच त्वरित विवरण।",
      stepperLabel: "उपयोग के मामले",
      moduleLabel: "मॉड्यूल",
      aiEngineLabel: "एआई इंजन",
      faqs: [
        { q: "क्या चिकित्सा एआई केवल EMR के ऊपर जोड़ा गया एक चैटबॉट है?", a: "नहीं। चिकित्सा एआई प्रत्येक कार्यप्रवाह के भीतर एकीकृत है। Voice-MD परामर्श के दौरान सीधे नोट लिखता है; Lipi ओपीडी में हस्तलिखित नुस्खे पढ़ता है; ClaimIQ दावा सबमिशन से पहले स्वचालित जांच चलाता है।" },
        { q: "Vaani AI किन भारतीय भाषाओं का समर्थन करता है?", a: "Vaani व्हाट्सएप, वेब और वॉयस पर 11 भाषाओं का समर्थन करता है, जिनमें हिंदी, अंग्रेजी, तमिल, तेलुगु, कन्नड़, मलयालम, मराठी, बंगाली, गुजराती, पंजाबी और उड़िया शामिल हैं।" },
        { q: "क्या Voice-MD के लिए किसी विशेष हार्डवेयर की आवश्यकता है?", a: "नहीं। Voice-MD उन्हीं कंप्यूटर और टैबलेट पर चलता है जिन्हें डॉक्टर डेस्क पहले से उपयोग करता है। एक मानक माइक पर्याप्त है।" },
        { q: "हस्तलिखित नुस्खों के लिए Lipi की सटीकता दर क्या है?", a: "दवा के नामों पर Lipi की उत्पादन सटीकता 96.4% है। सुरक्षा सुनिश्चित करने के लिए, कम आत्मविश्वास स्कोर वाले किसी भी नुस्खे को समीक्षा के लिए सीधे फार्मासिست के पास भेजा जाता है।" },
        { q: "क्या अस्पताल विशिष्ट विभागों के लिए एआई को बंद कर सकते हैं?", a: "हाँ। एआई सुविधाओं को प्रत्येक विभाग और भूमिका के अनुसार चालू या बंद किया जा सकता है। चिकित्सा निदेशक इसे सीधे व्यवस्थापक कंसोल से प्रबंधित कर सकते हैं।" },
        { q: "क्या मॉडल को प्रशिक्षित करने के लिए मरीज के डेटा का उपयोग किया जाता है?", a: "नहीं। मॉडल को अस्पताल के बाहर सुरक्षित रूप से पूर्व-प्रशिक्षित किया जाता है। आंतरिक रूप से, केवल वि-पहचाने गए और रोगी की सहमति वाले डेटा का उपयोग किया जाता है।" },
      ],
      cases: [
        {
          num: "यूज़ केस 01", numShort: "01", title: "अपॉइंटमेंट बुकिंग और रोगी सहायता डेस्क।", intro: "व्हाट्सएप, वेब और वॉयस पर एक 24×7 बहुभाषी बुकिंग एजेंट, जो आपकी वास्तविक समय सारणी और डॉक्टरों की उपलब्धता से संचालित होता है।",
          rows: [
            { dt: getProblem("hi"), dd: "फोन लाइनें व्यस्त होने और स्वागत पटल पर काम का अधिक दबाव होने से मरीज बीच में ही बुकिंग छोड़ देते हैं।" },
            { dt: getAi("hi"), dd: "Sahyogi अस्पताल के संदर्भ के साथ अपॉइंटमेंट बुकिंग, निर्धारण और बदलावों का प्रबंधन करता है।" },
            { dt: getImpact("hi"), dd: "↓ 38% कम ड्रॉप-ऑफ · 24×7 बुकिंग · स्वागत पटल की 4 गुना क्षमता वृद्धि", impact: true },
            { dt: getModule("hi"), dd: "अपॉइंटमेंट, QMS, पंजीकरण" },
            { dt: getAiCap("hi"), dd: "Sahyogi AI" },
          ],
          color: "#0A9E9A", accent: "rgba(10,158,154,0.08)", icon: "calendar", metricVal: "38%", metricLabel: "कम ड्रॉप-ऑफ", tag: "Sahyogi AI"
        },
        {
          num: "यूज़ केस 02", numShort: "02", title: "ओपीडी डॉक्टर डेस्क दस्तावेज़ीकरण।", intro: "डॉक्टर बोलते हैं; Voice-MD संरचित SOAP नोट्स, समस्या सूचियाँ और ऑर्डर सीधे EMR में लिख देता है।",
          rows: [
            { dt: getProblem("hi"), dd: "डॉक्टर ईएमआर में टाइप करने में 40% परामर्श समय गंवा देते हैं।" },
            { dt: getAi("hi"), dd: "Voice-MD वास्तविक समय में परामर्श का विवरण रिकॉर्ड, संरचित और कोडिंग करता है।" },
            { dt: getImpact("hi"), dd: "प्रति रोगी +9 मिनट की बचत · स्पष्ट नोट्स · डॉक्टरों के लिए आसान संचालन", impact: true },
            { dt: getModule("hi"), dd: "डॉक्टर डेस्क, EMR" },
            { dt: getAiCap("hi"), dd: "Voice-MD · क्लिनिकल कोडिंग AI" },
          ],
          color: "#2A9E5B", accent: "rgba(42,158,91,0.08)", icon: "micro", metricVal: "9 min", metricLabel: "बचाया समय", tag: "Voice-MD"
        },
        {
          num: "यूज़ केस 03", numShort: "03", title: "प्रिस्क्रिप्शन डिजिटलीकरण।", intro: "हस्तलिखित नुस्खे पढ़े जाते हैं, फ़ॉर्मूलरी के विरुद्ध सत्यापित होते हैं और स्वतः फार्मेसी को भेजे जाते हैं।",
          rows: [
            { dt: getProblem("hi"), dd: "कागजी नुस्खे फार्मेसी और बीमा वर्कफ़्लो में बाधा डालते हैं।" },
            { dt: getAi("hi"), dd: "Lipi दवा का नाम, खुराक, आवृत्ति और अवधि को फ़ॉर्मूलरी मैपिंग के साथ निकालता है।" },
            { dt: getImpact("hi"), dd: "96.4% दवा-नाम सटीकता · फार्मेसी और दावों को स्वतः प्रेषित", impact: true },
            { dt: getModule("hi"), dd: "ओपीडी, फार्मेसी, EMR" },
            { dt: getAiCap("hi"), dd: "Lipi AI" },
          ],
          color: "#B88500", accent: "rgba(184,133,0,0.08)", icon: "file", metricVal: "96.4%", metricLabel: "सटीकता", tag: "Lipi AI"
        },
        {
          num: "यूज़ केस 04", numShort: "04", title: "आईपीडी और नर्सिंग वर्कफ़्लो।", intro: "नर्सिंग राउंड, महत्वपूर्ण संकेत, MAR और नैदानिक ​​नोट्स को वॉयस द्वारा कैप्चर और संरचित किया जाता है।",
          rows: [
            { dt: getProblem("hi"), dd: "नर्सिंग नोट्स अक्सर असंगत होते हैं और शायद ही कभी एनालिटिक्स के लिए संरचित होते हैं।" },
            { dt: getAi("hi"), dd: "नर्सिंग के लिए Voice-MD: संरचित विटल्स, सेवन/निकासी, MAR और शिफ्ट हैंडओवर।" },
            { dt: getImpact("hi"), dd: "2.4 गुना तेज़ हैंडओवर · व्यावसायिक एनालिटिक्स के लिए संरचित नर्सिंग डेटा", impact: true },
            { dt: getModule("hi"), dd: "आईपीडी, नर्सिंग स्टेशन" },
            { dt: getAiCap("hi"), dd: "Voice-MD" },
          ],
          color: "#CC2B58", accent: "rgba(204,43,88,0.08)", icon: "micro", metricVal: "2.4×", metricLabel: "तेज़ हैंडओवर", tag: "Voice-MD"
        },
        {
          num: "यूज़ केस 05", numShort: "05", title: "डिस्चार्ज सारांश निर्माण।", intro: "प्रवेश समयरेखा से डिस्चार्ज सारांश स्वतः तैयार होते हैं, जो डॉक्टर की समीक्षा और हस्ताक्षर के लिए तैयार होते हैं।",
          rows: [
            { dt: getProblem("hi"), dd: "डिस्चार्ज सारांश तैयार करने में कई घंटे लगते हैं और बेड टर्नअराउंड बाधित होता है।" },
            { dt: getAi("hi"), dd: "EMR + ऑर्डर + नर्सिंग नोट्स से ऑटो-ड्राफ्ट सारांश, पूरी तरह से कोडित और हस्ताक्षर के लिए तैयार।" },
            { dt: getImpact("hi"), dd: "↓ 78% डिस्चार्ज समय · तेज़ बेड टर्नअराउंड · स्पष्ट दावे", impact: true },
            { dt: getModule("hi"), dd: "आईपीडी, डिस्चार्ज सारांश" },
            { dt: getAiCap("hi"), dd: "Lipi AI · क्लिनिकल कोडिंग AI" },
          ],
          color: "#053C61", accent: "rgba(5,60,97,0.07)", icon: "file", metricVal: "78%", metricLabel: "तेज़ डिस्चार्ज", tag: "Lipi + Coding"
        },
        {
          num: "यूज़ केस 06", numShort: "06", title: "ICD, LOINC और SNOMED कोडिंग सहायता।", intro: "मानव समीक्षा के लिए आत्मविश्वास स्कोर के साथ नोट्स, लैब और ऑर्डर अंतर्राष्ट्रीय मानकों के अनुसार स्वतः कोडित होते हैं।",
          rows: [
            { dt: getProblem("hi"), dd: "कोडर सीमित संख्या में हैं और कोड ही प्रत्येक परिणामी विश्लेषण को संचालित करते।" },
            { dt: labels.ai, dd: "उत्पत्ति और विश्वसनीयता स्कोर के साथ ICD-10, LOINC, SNOMED के लिए ऑटो-कोडिंग।" },
            { dt: labels.impact, dd: "↓ 72% कोडिंग बैकलॉग · बेहतर व्यावसायिक रिपोर्ट · दावों के बेहतर परिणाम", impact: true },
            { dt: labels.module, dd: "EMR, दावे, लैब" },
            { dt: labels.aiCap, dd: "क्लिनical कोडिंग AI" },
          ],
          color: "#0A9E9A", accent: "rgba(10,158,154,0.08)", icon: "chip", metricVal: "72%", metricLabel: "कम बैकलॉग", tag: "Clinical Coding AI"
        },
        {
          num: "यूज़ केस 07", numShort: "07", title: "NHCX / PMJAY दावा वर्कफ़्लो बुद्धिमत्ता।", intro: "ClaimIQ जमा करने से पहले दस्तावेजों, कोड और शुल्कों को सत्यापित करता है और अस्वीकृति जोखिम की भविष्यवाणी करता।",
          rows: [
            { dt: labels.problem, dd: "अस्वीकृति चक्र कुल दावा राजस्व का 22% हिस्सा खा जाते हैं और DSO बढ़ा देते हैं।" },
            { dt: labels.ai, dd: "स्वचालित सत्यापन, लापता दस्तावेज़ पहचान और अस्वीकृति-जोखिम स्कोरिंग।" },
            { dt: labels.impact, dd: "97% पहली बार में स्वीकृति · दावा चक्र में ↓ 11 दिनों की कमी · ↓ 64% कम रीवर्क", impact: true },
            { dt: labels.module, dd: "NHCX दावे, बिलिंग" },
            { dt: labels.aiCap, dd: "ClaimIQ" },
          ],
          color: "#2A9E5B", accent: "rgba(42,158,91,0.08)", icon: "shield", metricVal: "97%", metricLabel: "पहली बार में स्वीकृति", tag: "ClaimIQ"
        },
        {
          num: "यूज़ केस 08", numShort: "08", title: "सरल भाषा में अस्पताल विश्लेषण।", intro: "अस्पताल के अधिकारी प्रश्न पूछते हैं; AI MIS एजेंट चार्ट, ड्रिल-डाउन विवरण और डेटा स्रोत के साथ उत्तर देता है।",
          rows: [
            { dt: labels.problem, dd: "अधिकारी सरल प्रश्नों के उत्तर के लिए भी विश्लेषक टीम का दिनों तक इंतजार करते हैं।" },
            { dt: labels.ai, dd: "AI MIS एजेंट सत्यापन योग्य SQL कोड के साथ सरल भाषा में उत्तर प्रदान करता है।" },
            { dt: labels.impact, dd: "दिनों के बजाय सेकंड में समाधान · प्रत्येक अधिकारी के लिए त्वरित स्व-सेवा BI", impact: true },
            { dt: labels.module, dd: "MIS और BI एनालिटिक्स" },
            { dt: labels.aiCap, dd: "AI MIS / BI एजेंट" },
          ],
          color: "#B88500", accent: "rgba(184,133,0,0.08)", icon: "chart", metricVal: "<1s", metricLabel: "प्रतीक्षा समय में कमी", tag: "AI MIS Agent"
        },
        {
          num: "यूज़ केस 09", numShort: "09", title: "फार्मेसी और इन्वेंट्री बुद्धिमत्ता।", intro: "पूर्वानुमानित मांग, स्मार्ट रीऑर्डर सीमाएं, समाप्ति-जोखिम और वैकल्पिक दवा सुझाव, सब फार्मेसी में लाइव हैं।",
          rows: [
            { dt: labels.problem, dd: "स्टॉक समाप्त होने से मरीजों को नुकसान होता है; अत्यधिक स्टॉक नकदी को ब्लॉक करता है।" },
            { dt: labels.ai, dd: "मांग का पूर्वानुमान, रीऑर्डर पॉइंट और समाप्ति-जोخिम से जुड़े अलर्ट।" },
            { dt: labels.impact, dd: "↓ 31% कम स्टॉक-आउट · ↓ 18% दवाओं के खराब होने में कमी · तेज़ बिलिंग", impact: true },
            { dt: labels.module, dd: "फार्मेसी, इन्वेंट्री" },
            { dt: labels.aiCap, dd: "Sahyogi AI · ClaimIQ" },
          ],
          color: "#CC2B58", accent: "rgba(204,43,88,0.08)", icon: "sparkle", metricVal: "31%", metricLabel: "कम स्टॉक-आउट", tag: "Sahyogi + ClaimIQ"
        },
        {
          num: "यूज़ केस 10", numShort: "10", title: "रोगी जुड़ाव और अनुवर्ती कार्रवाई (Follow-up)।", intro: "दवाओं के पालन, स्वास्थ्य सुधार और फॉलो-अप के लिए व्हाट्सएप पर 11 भारतीय भाषाओं में बहुभाषी संदेश।",
          rows: [
            { dt: labels.problem, dd: "डिस्चार्ज के बाद रोगियों का फॉलो-अप पर बने रहने का दर 30% से भी कम हो जाता है।" },
            { dt: labels.ai, dd: "Vaani AI रोगी की भाषा में उनके संदर्भ के अनुसार संदेश भेजता है।" },
            { dt: labels.impact, dd: "+47% फॉलो-अप दर · बेहतर नैदानिक परिणाम · बेहतर रोगी संतुष्टि", impact: true },
            { dt: labels.module, dd: "रोगी जुड़ाव" },
            { dt: labels.aiCap, dd: "Vaani AI" },
          ],
          color: "#053C61", accent: "rgba(5,60,97,0.07)", icon: "chat", metricVal: "+47%", metricLabel: "फॉलो-अप दर", tag: "Vaani AI"
        },
      ],
    };
  }

  // Spanish mappings
  if (locale === "es") {
    const labels = {
      problem: getProblem("es"),
      ai: getAi("es"),
      impact: getImpact("es"),
      module: getModule("es"),
      aiCap: getAiCap("es"),
    };
    return {
      heroEyebrow: "IA en sus flujos",
      heroTitle: "IA que resuelve problemas hospitalarios reales.",
      heroDesc: "Chikitsa integra automatización con IA directamente en la facturación, documentación y consultorios. La automatización de la IA abarca desde la reserva de citas y la documentación de OPD hasta las reclamaciones, los resúmenes de alta, la analítica y el compromiso del paciente.",
      bookDemo: "Reservar Demo",
      faqsEyebrow: "Preguntas Frecuentes",
      faqsTitle: "Respuestas sobre el flujo de trabajo de IA.",
      faqsDesc: "Cinco respuestas rápidas sobre cómo Chikitsa maneja la privacidad de datos, los modelos y la precisión clínica.",
      stepperLabel: "Casos de Uso",
      moduleLabel: "Módulo",
      aiEngineLabel: "Capacidad IA",
      faqs: [
        { q: "¿Es la IA de Chikitsa solo un chatbot agregado sobre el EMR?", a: "No. La IA de Chikitsa está integrada dentro de cada flujo de trabajo. Voice-MD redacta notas directamente durante la consulta; Lipi lee recetas manuscritas en la farmacia; ClaimIQ ejecuta controles automáticos antes de enviar reclamaciones." },
        { q: "¿Qué idiomas admite Vaani AI?", a: "Vaani admite 11 idiomas en WhatsApp, web y voz, incluidos hindi, inglés, tamil, telugu, kannada, malayalam, maratí, bengalí, gujarati, punyabí y oriya." },
        { q: "¿Se necesita algún hardware especial para Voice-MD?", a: "No. Voice-MD se ejecuta en las mismas computadoras y tabletas que ya se usan en el escritorio del médico. Un micrófono estándar es suficiente." },
        { q: "¿Cuál es la tasa de precisión de Lipi para recetas manuscritas?", a: "La precisión de Lipi en nombres de medicamentos es del 96.4%. Para garantizar la seguridad, cualquier receta con bajo puntaje de confianza se envía al farmacéutico para su revisión." },
        { q: "¿Pueden los hospitales desactivar la IA para departamentos específicos?", a: "Sí. Las funciones de IA se pueden activar o desactivar según el departamento y el rol, gestionado directamente desde la consola de administración." },
        { q: "¿Se utilizan los datos del paciente para entrenar los modelos?", a: "No. Los modelos se preentrenan de forma segura fuera del hospital. Internamente, solo se utilizan datos anonimizados con el consentimiento del paciente." },
      ],
      cases: [
        {
          num: "Caso de Uso 01", numShort: "01", title: "Reserva de citas y mesa de ayuda para pacientes.", intro: "Un agente de reservas multilingüe 24×7 en WhatsApp, web y voz, integrado con su agenda en tiempo real y disponibilidad médica.",
          rows: [
            { dt: labels.problem, dd: "Los pacientes abandonan las reservas cuando las líneas telefónicas están ocupadas y las recepciones abrumadas." },
            { dt: labels.ai, dd: "Sahyogi gestiona reservas, programación y cambios con contexto hospitalario." },
            { dt: labels.impact, dd: "↓ 38% menos abandonos · Reservas 24×7 · Capacidad de recepción 4 veces mayor", impact: true },
            { dt: labels.module, dd: "Citas, QMS, Registro" },
            { dt: labels.aiCap, dd: "Sahyogi AI" },
          ],
          color: "#0A9E9A", accent: "rgba(10,158,154,0.08)", icon: "calendar", metricVal: "38%", metricLabel: "menos abandonos", tag: "Sahyogi AI"
        },
        {
          num: "Caso de Uso 02", numShort: "02", title: "Documentación en el escritorio del médico de OPD.", intro: "Los médicos hablan; Voice-MD redacta notas SOAP estructuradas, listas de problemas y órdenes directamente en el EMR.",
          rows: [
            { dt: labels.problem, dd: "Los médicos pierden el 40% del tiempo de consulta escribiendo en el EMR." },
            { dt: labels.ai, dd: "Voice-MD registra, estructura y codifica la consulta en tiempo real." },
            { dt: labels.impact, dd: "+9 minutos ahorrados por paciente · Notas claras · Fácil adopción médica", impact: true },
            { dt: labels.module, dd: "Escritorio del médico, EMR" },
            { dt: labels.aiCap, dd: "Voice-MD · IA de Codificación Clínica" },
          ],
          color: "#2A9E5B", accent: "rgba(42,158,91,0.08)", icon: "micro", metricVal: "9 min", metricLabel: "tiempo ahorrado", tag: "Voice-MD"
        },
        {
          num: "Caso de Uso 03", numShort: "03", title: "Digitalización de recetas.", intro: "Recetas manuscritas digitalizadas automáticamente, validadas contra el vademécum y enviadas a la farmacia.",
          rows: [
            { dt: labels.problem, dd: "Las recetas en papel interrumpen los flujos de la farmacia y de las reclamaciones de seguros." },
            { dt: labels.ai, dd: "Lipi extrae nombre del fármaco, dosis, frecuencia y duración con mapeo del vademécum." },
            { dt: labels.impact, dd: "96.4% precisión en fármacos · Enrutamiento automático a farmacia y reclamaciones", impact: true },
            { dt: labels.module, dd: "OPD, Farmacia, EMR" },
            { dt: labels.aiCap, dd: "Lipi AI" },
          ],
          color: "#B88500", accent: "rgba(184,133,0,0.08)", icon: "file", metricVal: "96.4%", metricLabel: "precisión", tag: "Lipi AI"
        },
        {
          num: "Caso de Uso 04", numShort: "04", title: "Flujos de IPD y enfermería.", intro: "Rondas de enfermería, signos vitales, MAR y notas clínicas capturadas por voz y estructuradas por IA.",
          rows: [
            { dt: labels.problem, dd: "Las notas de enfermería suelen ser inconsistentes y rara vez se estructuran para análisis." },
            { dt: labels.ai, dd: "Voice-MD para enfermería: signos vitales estructurados, balance de ingresos/gastos, MAR y entregas de turno." },
            { dt: labels.impact, dd: "2.4 veces más rápida la entrega · Datos de enfermería estructurados para analítica", impact: true },
            { dt: labels.module, dd: "IPD, Estación de Enfermería" },
            { dt: labels.aiCap, dd: "Voice-MD" },
          ],
          color: "#CC2B58", accent: "rgba(204,43,88,0.08)", icon: "micro", metricVal: "2.4×", metricLabel: "entrega rápida", tag: "Voice-MD"
        },
        {
          num: "Caso de Uso 05", numShort: "05", title: "Generación de informes de alta.", intro: "Los resúmenes de alta se redactan automáticamente a partir de la línea de tiempo de hospitalización, listos para revisión médica.",
          rows: [
            { dt: labels.problem, dd: "Redactar informes de alta toma horas y retrasa la rotación de camas." },
            { dt: labels.ai, dd: "Borrador de resumen automático desde EMR + órdenes + notas de enfermería, codificado y listo para firma." },
            { dt: labels.impact, dd: "↓ 78% tiempo de alta · Rotación de camas más rápida · Reclamaciones limpias", impact: true },
            { dt: labels.module, dd: "IPD, Resumen de Alta" },
            { dt: labels.aiCap, dd: "Lipi AI · IA de Codificación Clínica" },
          ],
          color: "#053C61", accent: "rgba(5,60,97,0.07)", icon: "file", metricVal: "78%", metricLabel: "alta más rápida", tag: "Lipi + Coding"
        },
        {
          num: "Caso de Uso 06", numShort: "06", title: "Asistencia de codificación ICD, LOINC y SNOMED.", intro: "Notas, laboratorios y órdenes codificados automáticamente según estándares internacionales para revisión humana.",
          rows: [
            { dt: labels.problem, dd: "Los codificadores son escasos y de ellos depende la velocidad de facturación." },
            { dt: labels.ai, dd: "Codificación automática a ICD-10, LOINC, SNOMED con puntajes de confianza." },
            { dt: labels.impact, dd: "↓ 72% en el retraso de codificación · Mejor BI clínico · Reclamaciones optimizadas", impact: true },
            { dt: labels.module, dd: "EMR, Reclamaciones, Laboratorio" },
            { dt: labels.aiCap, dd: "IA de Codificación Clínica" },
          ],
          color: "#0A9E9A", accent: "rgba(10,158,154,0.08)", icon: "chip", metricVal: "72%", metricLabel: "menos retraso", tag: "Clinical Coding AI"
        },
        {
          num: "Caso de Uso 07", numShort: "07", title: "Inteligencia de flujo de reclamaciones NHCX / PMJAY.", intro: "ClaimIQ valida documentos, códigos y tarifas antes del envío para predecir el riesgo de rechazo.",
          rows: [
            { dt: labels.problem, dd: "Los ciclos de rechazo consumen el 22% de los ingresos por reclamaciones y aumentan el DSO." },
            { dt: labels.ai, dd: "Validación automatizada, detección de documentos faltantes y puntuación de riesgo de rechazo." },
            { dt: labels.impact, dd: "97% aprobación en primer intento · ↓ 11 días en ciclo de cobro · ↓ 64% menos reprocesamiento", impact: true },
            { dt: labels.module, dd: "Reclamaciones NHCX, Facturación" },
            { dt: labels.aiCap, dd: "ClaimIQ" },
          ],
          color: "#2A9E5B", accent: "rgba(42,158,91,0.08)", icon: "shield", metricVal: "97%", metricLabel: "primer intento", tag: "ClaimIQ"
        },
        {
          num: "Caso de Uso 08", numShort: "08", title: "Analítica hospitalaria en lenguaje sencillo.", intro: "Los directivos hacen preguntas; el agente AI MIS responde con gráficos, detalles interactivos y origen de datos.",
          rows: [
            { dt: labels.problem, dd: "Los directivos esperan días por respuestas a métricas operativas simples." },
            { dt: labels.ai, dd: "El agente AI MIS traduce preguntas a consultas SQL seguras para responder en segundos." },
            { dt: labels.impact, dd: "Resolución en segundos en lugar de días · BI de autoservicio instantáneo para cada líder", impact: true },
            { dt: labels.module, dd: "MIS y BI Analítica" },
            { dt: labels.aiCap, dd: "Agente AI MIS / BI" },
          ],
          color: "#B88500", accent: "rgba(184,133,0,0.08)", icon: "chart", metricVal: "<1s", metricLabel: "tiempo de espera", tag: "AI MIS Agent"
        },
        {
          num: "Caso de Uso 09", numShort: "09", title: "Inteligencia en farmacia e inventario.", intro: "Previsión de demanda, puntos de reorden inteligentes, alertas de vencimiento y sugerencias de alternativas de fármacos.",
          rows: [
            { dt: labels.problem, dd: "El desabastecimiento afecta la atención; el exceso de stock vence en los estantes." },
            { dt: labels.ai, dd: "Previsión de demanda, niveles de seguridad por vencimiento y alertas integradas." },
            { dt: labels.impact, dd: "↓ 31% menos desabastecimiento · ↓ 18% menos mermas de medicamentos · Facturación fluida", impact: true },
            { dt: labels.module, dd: "Farmacia, Inventario" },
            { dt: labels.aiCap, dd: "Sahyogi AI · ClaimIQ" },
          ],
          color: "#CC2B58", accent: "rgba(204,43,88,0.08)", icon: "sparkle", metricVal: "31%", metricLabel: "menos quiebres", tag: "Sahyogi + ClaimIQ"
        },
        {
          num: "Caso de Uso 10", numShort: "10", title: "Compromiso y seguimiento de pacientes.", intro: "Recordatorios interactivos de adherencia a medicamentos, recuperación y seguimiento por WhatsApp en el idioma nativo del paciente.",
          rows: [
            { dt: labels.problem, dd: "La tasa de seguimiento de pacientes cae por debajo del 30% después del alta." },
            { dt: labels.ai, dd: "Vaani AI interactúa y envía recordatorios en la lengua preferida del paciente." },
            { dt: labels.impact, dd: "+47% en tasa de seguimiento · Mejores resultados clínicos · Mayor satisfacción", impact: true },
            { dt: labels.module, dd: "Compromiso del Paciente" },
            { dt: labels.aiCap, dd: "Vaani AI" },
          ],
          color: "#053C61", accent: "rgba(5,60,97,0.07)", icon: "chat", metricVal: "+47%", metricLabel: "tasa de seguimiento", tag: "Vaani AI"
        },
      ],
    };
  }

  // French mappings
  if (locale === "fr") {
    const labels = {
      problem: getProblem("fr"),
      ai: getAi("fr"),
      impact: getImpact("fr"),
      module: getModule("fr"),
      aiCap: getAiCap("fr"),
    };
    return {
      heroEyebrow: "L'IA dans vos flux",
      heroTitle: "Une IA qui résout de vrais problèmes hospitaliers.",
      heroDesc: "Chikitsa intègre l'automatisation IA directement dans la facturation, l'admission et les consultations. L'automatisation par l'IA s'étend de la réservation de rendez-vous et de la documentation OPD aux réclamations d'assurance, aux résumés de sortie, à l'analyse et à l'engagement des patients.",
      bookDemo: "Réserver Démo",
      faqsEyebrow: "Questions Fréquentes",
      faqsTitle: "Réponses sur le flux de travail de l'IA.",
      faqsDesc: "Cinq réponses rapides sur la façon dont Chikitsa gère la confidentialité des données, les modèles et la précision clinique.",
      stepperLabel: "Cas d'Usage",
      moduleLabel: "Module",
      aiEngineLabel: "Capacité IA",
      faqs: [
        { q: "Est-ce que l'IA de Chikitsa est juste un chatbot ajouté sur le dossier médical ?", a: "Non. L'IA de Chikitsa est intégrée au cœur de chaque flux. Voice-MD rédige des notes pendant la consultation ; Lipi déchiffre les ordonnances manuscrites en pharmacie ; ClaimIQ effectue des contrôles automatiques avant l'envoi des réclamations." },
        { q: "Quelles langues Vaani AI prend-elle en charge ?", a: "Vaani prend en charge 11 langues sur WhatsApp, le web et la voix, y compris l'hindi, l'anglais, le tamoul, le télougou, le kannada, le malayalam, le marathi, le bengali, le gujarati, le pendjabi et l'odia." },
        { q: "Un matériel spécial est-il requis pour Voice-MD ?", a: "Non. Voice-MD fonctionne sur les mêmes ordinateurs et tablettes que les médecins utilisent déjà. Un microphone standard suffit." },
        { q: "Quel est le taux de précision de Lipi pour les ordonnances manuscrites ?", a: "La précision de Lipi sur les noms de médicaments est de 96,4 %. Pour des raisons de sécurité, toute ordonnance ayant un faible score de confiance est envoyée au pharmacien pour révision." },
        { q: "Les hôpitaux peuvent-ils désactiver l'IA pour certains services ?", a: "Oui. Les fonctionnalités d'IA peuvent être activées ou désactivées par service et par rôle, directement depuis la console d'administration." },
        { q: "Les données des patients sont-elles utilisées pour entraîner les modèles ?", a: "Non. Les modèles sont pré-entraînés de manière sécurisée en dehors de l'hôpital. En interne, seules les données anonymisées et consenties sont utilisées." },
      ],
      cases: [
        {
          num: "Cas d'Usage 01", numShort: "01", title: "Réservation de rendez-vous et bureau d'accueil des patients.", intro: "Un agent de réservation multilingue 24h/24 et 7j/7 sur WhatsApp, le web et la voix, connecté à vos plannings en temps réel.",
          rows: [
            { dt: labels.problem, dd: "Les patients abandonnent les réservations lorsque les lignes téléphoniques sont occupées et l'accueil débordé." },
            { dt: labels.ai, dd: "Sahyogi trie, planifie et reporte les rendez-vous avec le contexte hospitalier." },
            { dt: labels.impact, dd: "↓ 38% de baisse des abandons · Réservation 24/7 · Capacité d'accueil multipliée par 4", impact: true },
            { dt: labels.module, dd: "Rendez-vous, QMS, Enregistrement" },
            { dt: labels.aiCap, dd: "Sahyogi AI" },
          ],
          color: "#0A9E9A", accent: "rgba(10,158,154,0.08)", icon: "calendar", metricVal: "38%", metricLabel: "moins d'abandons", tag: "Sahyogi AI"
        },
        {
          num: "Cas d'Usage 02", numShort: "02", title: "Documentation au bureau du médecin OPD.", intro: "Les médecins parlent ; Voice-MD rédige des notes SOAP structurées, des listes de problèmes et des prescriptions directement dans le dossier médical.",
          rows: [
            { dt: labels.problem, dd: "Les médecins perdent 40 % du temps de consultation à saisir des données sur ordinateur." },
            { dt: labels.ai, dd: "Voice-MD capture, structure et code la consultation en temps réel." },
            { dt: labels.impact, dd: "+9 min gagnées par patient · Notes de meilleure qualité · Adoption médicale immédiate", impact: true },
            { dt: labels.module, dd: "Bureau du médecin, Dossier Médical" },
            { dt: labels.aiCap, dd: "Voice-MD · IA de Codage Clinique" },
          ],
          color: "#2A9E5B", accent: "rgba(42,158,91,0.08)", icon: "micro", metricVal: "9 min", metricLabel: "temps gagné", tag: "Voice-MD"
        },
        {
          num: "Cas d'Usage 03", numShort: "03", title: "Numérisation des prescriptions.", intro: "Les ordonnances manuscrites sont lues, vérifiées par rapport au dictionnaire d'ordonnances et envoyées automatiquement à la pharmacie.",
          rows: [
            { dt: labels.problem, dd: "Les ordonnances papier ralentissent les flux de la pharmacie et des réclamations d'assurance." },
            { dt: labels.ai, dd: "Lipi extrait le nom du médicament, le dosage, la fréquence et la durée avec correspondance automatique." },
            { dt: labels.impact, dd: "Précision de 96,4% sur les médicaments · Transmission automatique pharmacie et réclamations", impact: true },
            { dt: labels.module, dd: "OPD, Pharmacie, Dossier Médical" },
            { dt: labels.aiCap, dd: "Lipi AI" },
          ],
          color: "#B88500", accent: "rgba(184,133,0,0.08)", icon: "file", metricVal: "96.4%", metricLabel: "précision", tag: "Lipi AI"
        },
        {
          num: "Cas d'Usage 04", numShort: "04", title: "Flux IPD et soins infirmiers.", intro: "Les tournées infirmières, constantes vitales, MAR et notes cliniques sont dictés à la voix et structurés par l'IA.",
          rows: [
            { dt: labels.problem, dd: "Les notes infirmières sont souvent incohérentes et rarement structurées pour analyse." },
            { dt: labels.ai, dd: "Voice-MD pour les soins : constantes, entrées/sorties, MAR et transmissions de garde structurés." },
            { dt: labels.impact, dd: "Transmissions de garde 2,4× plus rapides · Données de soins structurées pour les analyses", impact: true },
            { dt: labels.module, dd: "IPD, Poste de Soins" },
            { dt: labels.aiCap, dd: "Voice-MD" },
          ],
          color: "#CC2B58", accent: "rgba(204,43,88,0.08)", icon: "micro", metricVal: "2.4×", metricLabel: "transmission rapide", tag: "Voice-MD"
        },
        {
          num: "Cas d'Usage 05", numShort: "05", title: "Génération des rapports de sortie.", intro: "Les résumés de sortie sont rédigés automatiquement à partir du dossier d'hospitalisation, prêts pour révision médicale.",
          rows: [
            { dt: labels.problem, dd: "La rédaction des résumés de sortie prend des heures et retarde la libération des lits." },
            { dt: labels.ai, dd: "Projet de résumé rédigé automatiquement depuis le dossier médical, codé et prêt pour signature." },
            { dt: labels.impact, dd: "↓ 78% de temps de sortie · Libération plus rapide des lits · Réclamations d'assurance claires", impact: true },
            { dt: labels.module, dd: "IPD, Résumé de Sortie" },
            { dt: labels.aiCap, dd: "Lipi AI · IA de Codage Clinique" },
          ],
          color: "#053C61", accent: "rgba(5,60,97,0.07)", icon: "file", metricVal: "78%", metricLabel: "sortie plus rapide", tag: "Lipi + Coding"
        },
        {
          num: "Cas d'Usage 06", numShort: "06", title: "Aide au codage CIM-10, LOINC et SNOMED.", intro: "Les notes, analyses et prescriptions sont codés automatiquement selon les normes internationales pour révision humaine.",
          rows: [
            { dt: labels.problem, dd: "Les codeurs médicaux sont rares et déterminent la vitesse de facturation." },
            { dt: labels.ai, dd: "Codage automatique vers CIM-10, LOINC, SNOMED avec scores de confiance." },
            { dt: labels.impact, dd: "↓ 72% d'arriéré de codage · Meilleure analyse clinique · Réclamations optimisées", impact: true },
            { dt: labels.module, dd: "Dossier Médical, Réclamations, Labo" },
            { dt: labels.aiCap, dd: "IA de Codage Clinique" },
          ],
          color: "#0A9E9A", accent: "rgba(10,158,154,0.08)", icon: "chip", metricVal: "72%", metricLabel: "moins d'arriéré", tag: "Clinical Coding AI"
        },
        {
          num: "Cas d'Usage 07", numShort: "07", title: "Intelligence des flux de réclamations NHCX / PMJAY.", intro: "ClaimIQ valide les documents, les codes et les tarifs avant envoi pour prédire les risques de rejet.",
          rows: [
            { dt: labels.problem, dd: "Les cycles de rejet absorbent 22% des revenus de réclamation et augmentent le délai de paiement." },
            { dt: labels.ai, dd: "Validation automatisée, détection de documents manquants et score de risque de rejet." },
            { dt: labels.impact, dd: "97% d'approbation au premier envoi · ↓ 11 jours sur le cycle · ↓ 64% de retravail en moins", impact: true },
            { dt: labels.module, dd: "Réclamations NHCX, Facturation" },
            { dt: labels.aiCap, dd: "ClaimIQ" },
          ],
          color: "#2A9E5B", accent: "rgba(42,158,91,0.08)", icon: "shield", metricVal: "97%", metricLabel: "premier envoi", tag: "ClaimIQ"
        },
        {
          num: "Cas d'Usage 08", numShort: "08", title: "Analyses hospitalières en langage naturel.", intro: "Les directeurs posent des questions ; l'agent AI MIS répond avec des graphiques, des détails et l'origine des données.",
          rows: [
            { dt: labels.problem, dd: "Les décideurs attendent des jours pour obtenir des réponses à des questions opérationnelles simples." },
            { dt: labels.ai, dd: "L'agent AI MIS traduit les questions en requêtes SQL sécurisées pour y répondre en quelques secondes." },
            { dt: labels.impact, dd: "Résolution en secondes plutôt qu'en jours · Analyse en libre-service instantanée pour chaque leader", impact: true },
            { dt: labels.module, dd: "Analyses MIS & BI" },
            { dt: labels.aiCap, dd: "Agent AI MIS / BI" },
          ],
          color: "#B88500", accent: "rgba(184,133,0,0.08)", icon: "chart", metricVal: "<1s", metricLabel: "temps d'attente", tag: "AI MIS Agent"
        },
        {
          num: "Cas d'Usage 09", numShort: "09", title: "Intelligence pharmacie et stocks.", intro: "Prévision de la demande, points de commande intelligents, alertes de péremption et suggestions de substituts.",
          rows: [
            { dt: labels.problem, dd: "Les ruptures nuisent aux soins ; le surstockage périme sur les étagères." },
            { dt: labels.ai, dd: "Prévision de la demande, seuils de sécurité de péremption et alertes intégrées." },
            { dt: labels.impact, dd: "↓ 31% de ruptures de stock · ↓ 18% de pertes de médicaments · Facturation fluide", impact: true },
            { dt: labels.module, dd: "Pharmacie, Inventaire" },
            { dt: labels.aiCap, dd: "Sahyogi AI · ClaimIQ" },
          ],
          color: "#CC2B58", accent: "rgba(204,43,88,0.08)", icon: "sparkle", metricVal: "31%", metricLabel: "moins de ruptures", tag: "Sahyogi + ClaimIQ"
        },
        {
          num: "Cas d'Usage 10", numShort: "10", title: "Suivi et engagement des patients.", intro: "Rappels d'observance, suivi post-sortie et messages de rétablissement sur WhatsApp dans la langue du patient.",
          rows: [
            { dt: labels.problem, dd: "Le taux de suivi des patients tombe en dessous de 30 % après la sortie." },
            { dt: labels.ai, dd: "Vaani AI échange et envoie des rappels dans la langue préférée du patient." },
            { dt: labels.impact, dd: "+47% de taux de suivi · Meilleurs résultats cliniques · Plus grande satisfaction", impact: true },
            { dt: labels.module, dd: "Engagement Patient" },
            { dt: labels.aiCap, dd: "Vaani AI" },
          ],
          color: "#053C61", accent: "rgba(5,60,97,0.07)", icon: "chat", metricVal: "+47%", metricLabel: "taux de suivi", tag: "Vaani AI"
        },
      ],
    };
  }

  // Arabic mappings
  if (locale === "ar") {
    return {
      heroEyebrow: "الذكاء الاصطناعي في سير عملك",
      heroTitle: "ذكاء اصطناعي يحل مشاكل المستشفيات الحقيقية.",
      heroDesc: "يجمع تشيكيتسا أتمتة الذكاء الاصطناعي في صلب عمليات المستشفى، من حجز المواعيد والتوثيق السريري إلى المطالبات وتلخيص الخروج والتحليلات.",
      bookDemo: "احجز عرضاً تجريبياً",
      faqsEyebrow: "الأسئلة الشائعة",
      faqsTitle: "الأسئلة الشائعة حول الذكاء الاصطناعي في العمليات.",
      faqsDesc: "خمس إجابات سريعة حول أمان البيانات وتدريب النماذج والدقة السريرية في تشيكيتسا.",
      stepperLabel: "مسارات العمل",
      moduleLabel: "الوحدة",
      aiEngineLabel: "محرك الذكاء الاصطناعي",
      faqs: [
        { q: "هل ذكاء تشيكيتسا مجرد روبوت محادثة مضاف للنظام؟", a: "لا. يعمل الذكاء الاصطناعي في تشيكيتسا كجزء مدمج في الشاشات والعمليات اليومية للفريق. Voice-MD يكتب مع الطبيب أثناء الاستشارة، Lipi يرقمن الوصفات بالصيدلية، و ClaimIQ يدقق المطالبة قبل إرسالها. لا يوجد نظام منفصل." },
        { q: "ما هي اللغات التي يدعمها وكيل Vaani AI؟", a: "يدعم 11 لغة هندية وعالمية عبر الواتساب والويب والاتصال الصوتي لضمان وصول التنبيهات بالشكل المناسب." },
      ],
      cases: [
        {
          num: "سير العمل 01", numShort: "01", title: "حجز المواعيد ومكتب مساعدة المرضى.", intro: "وكيل حجز متعدد اللغات على مدار الساعة عبر الواتساب والويب والصوت، متصل بجدول المواعيد الفعلي للأطباء.",
          rows: [
            { dt: getProblem("ar"), dd: "يتخلى المرضى عن الحجز عندما تكون خطوط الهاتف مشغولة ومكاتب الاستقبال مزدحمة." },
            { dt: getAi("ar"), dd: "يقوم Sahyogi بفرز وحجز وإعادة جدولة المواعيد مع مراعاة سياق المستشفى." },
            { dt: getImpact("ar"), dd: "↓ 38% أقل تخلٍ عن الحجز · حجز 24/7 · زيادة قدرة الاستقبال 4 أضعاف", impact: true },
            { dt: getModule("ar"), dd: "المواعيد، إدارة الطوابير، التسجيل" },
            { dt: getAiCap("ar"), dd: "Sahyogi AI" },
          ],
          color: "#0A9E9A", accent: "rgba(10,158,154,0.08)", icon: "calendar", metricVal: "38%", metricLabel: "انخفاض التخلي عن الحجز", tag: "Sahyogi AI"
        },
        {
          num: "سير العمل 02", numShort: "02", title: "توثيق الطبيب في العيادات الخارجية.", intro: "يتحدث الأطباء؛ ويقوم مساعد Voice-MD بكتابة ملاحظات SOAP والطلبات الطبية مباشرة في السجل الطبي.",
          rows: [
            { dt: getProblem("ar"), dd: "يفقد الأطباء 40% من وقت الاستشارة في الكتابة اليدوية على الأنظمة." },
            { dt: getAi("ar"), dd: "يقوم مساعد Voice-MD بالتقاط وترميز محتوى الاستشارة الطبية بالوقت الفعلي." },
            { dt: getImpact("ar"), dd: "توفير +9 دقائق لكل مريض · ملفات صحية منظمة · سهولة التشغيل للأطباء", impact: true },
            { dt: getModule("ar"), dd: "مكتب الطبيب، السجل الطبي" },
            { dt: getAiCap("ar"), dd: "Voice-MD · الترميز السريري" },
          ],
          color: "#2A9E5B", accent: "rgba(42,158,91,0.08)", icon: "micro", metricVal: "9 دقائق", metricLabel: "saved", tag: "Voice-MD"
        },
        {
          num: "سير العمل 03", numShort: "03", title: "رقمة الوصفات الطبية.", intro: "قراءة الوصفات المكتوبة بخط اليد وتدقيقها وصرفها تلقائياً بالصيدلية.",
          rows: [
            { dt: getProblem("ar"), dd: "الوصفات الورقية تعطل العمليات وتؤخر تسليم الدواء." },
            { dt: getAi("ar"), dd: "يستخرج Lipi الجرعات والأدوية تلقائياً ويطابقها مع قائمة صيدلية المستشفى." },
            { dt: getImpact("ar"), dd: "دقة 96.4% في مطابقة الأدوية · صرف فوري وآلي بالصيدلية والمطالبات", impact: true },
            { dt: getModule("ar"), dd: "العيادات الخارجية، الصيدلية، السجل الطبي" },
            { dt: getAiCap("ar"), dd: "Lipi AI" },
          ],
          color: "#B88500", accent: "rgba(184,133,0,0.08)", icon: "file", metricVal: "96.4%", metricLabel: "دقة مطابقة الأدوية", tag: "Lipi AI"
        },
        {
          num: "سير العمل 04", numShort: "04", title: "أتمتة تمريض الأقسام الداخلية.", intro: "رصد جولات التمريض والعلامات الحيوية والملاحظات السريرية بالصوت.",
          rows: [
            { dt: getProblem("ar"), dd: "الملاحظات التمريضية الورقية غير منظمة ويصعب أرشفتها للتحليل." },
            { dt: getAi("ar"), dd: "مساعد التمريض الصوتي Voice-MD: تسجيل العلامات الحيوية، MAR، وتسليم المناوبات." },
            { dt: getImpact("ar"), dd: "تسليم مناوبات أسرع بـ 2.4 مرة · بيانات تمريض مهيكلة للتحليلات", impact: true },
            { dt: getModule("ar"), dd: "الأقسام الداخلية، محطة التمريض" },
            { dt: getAiCap("ar"), dd: "Voice-MD" },
          ],
          color: "#CC2B58", accent: "rgba(204,43,88,0.08)", icon: "micro", metricVal: "2.4×", metricLabel: "تسليم مناوبات أسرع", tag: "Voice-MD"
        },
        {
          num: "سير العمل 05", numShort: "05", title: "صياغة ملخصات الخروج.", intro: "صياغة تلقائية لملخص الخروج بناءً على السجل الطبي لتسهيل مراجعة الطبيب وتوقيعه.",
          rows: [
            { dt: getProblem("ar"), dd: "تأخر صياغة أوراق الخروج يتسبب في تعطيل تدوير الأسرة للمرضى الجدد." },
            { dt: getAi("ar"), dd: "توليد ملخص خروج منظم من ملف المريض الطبي وأوامر الأطباء وملاحظات التمريض." },
            { dt: getImpact("ar"), dd: "انخفاض 78% في زمن صياغة الخروج · تدوير أسرع للأسرة · مطالبات خالية من الأخطاء", impact: true },
            { dt: getModule("ar"), dd: "الأقسام الداخلية، ملخص الخروج" },
            { dt: getAiCap("ar"), dd: "Lipi AI · الترميز السريري" },
          ],
          color: "#053C61", accent: "rgba(5,60,97,0.07)", icon: "file", metricVal: "78%", metricLabel: "إجراءات خروج أسرع", tag: "Lipi + Coding"
        },
        {
          num: "سير العمل 06", numShort: "06", title: "مساعد الترميز الطبي الدولي.", intro: "توليد وتصنيف أكواد التشخيص والإجراءات الطبية وفق المعايير الدولية.",
          rows: [
            { dt: getProblem("ar"), dd: "ندرة الكوادر المؤهلة للترميز الطبي وتأثير جودة الكود على تحصيل الفواتير." },
            { dt: getAi("ar"), dd: "ترميز تلقائي لـ ICD-10 و LOINC و SNOMED مع نسب ثقة قابلة للمراجعة البشرية." },
            { dt: getImpact("ar"), dd: "تقليص 72% في تراكم ملفات الترميز · دقة أعلى في الفوترة والمطالبات", impact: true },
            { dt: getModule("ar"), dd: "السجل الطبي، المطالبات، المختبر" },
            { dt: getAiCap("ar"), dd: "الترميز الطبي بالذكاء الاصطناعي" },
          ],
          color: "#0A9E9A", accent: "rgba(10,158,154,0.08)", icon: "chip", metricVal: "72%", metricLabel: "تقليص تراكم الترميز", tag: "Clinical Coding AI"
        },
        {
          num: "سير العمل 07", numShort: "07", title: "ذكاء مطالبات التأمين و NHCX.", intro: "يقوم نظام ClaimIQ بالتحقق من الملفات الطبية والفواتير الطبية قبل إرسالها لشركات التأمين.",
          rows: [
            { dt: getProblem("ar"), dd: "دورات الرفض والتأخير تؤثر على التدفق المالي للمستشفى." },
            { dt: getAi("ar"), dd: "تحديد النواقص في الملفات وحساب نسب مخاطر الرفض للمطالبة." },
            { dt: getImpact("ar"), dd: "قبول 97% من المرة الأولى · تقليص 11 يوماً في دورة التحصيل · تقليل 64% في التعديل", impact: true },
            { dt: getModule("ar"), dd: "مطالبات التأمين، الفوترة" },
            { dt: getAiCap("ar"), dd: "ClaimIQ" },
          ],
          color: "#2A9E5B", accent: "rgba(42,158,91,0.08)", icon: "shield", metricVal: "97%", metricLabel: "قبول من أول مرة", tag: "ClaimIQ"
        },
        {
          num: "سير العمل 08", numShort: "08", title: "التقارير الإدارية بلغة مبسطة.", intro: "يجيب وكيل ذكاء الأعمال المباشر على استفسارات إدارة المستشفى بشكل فوري ورسوم تفاعلية.",
          rows: [
            { dt: getProblem("ar"), dd: "انتظار الإدارة لعدة أيام للحصول على إجابات لأسئلة تشغيلية بسيطة." },
            { dt: getAi("ar"), dd: "وكيل تحليل البيانات يجيب على الأسئلة بلغة مبسطة مع توليد استعلامات SQL آمنة تلقائياً." },
            { dt: getImpact("ar"), dd: "من أيام إلى ثوانٍ معدودة · تحليلات الخدمة الذاتية لكل قائد مستشفى", impact: true },
            { dt: getModule("ar"), dd: "التحليلات وذكاء الأعمال" },
            { dt: getAiCap("ar"), dd: "وكيل تحليل البيانات بالذكاء الاصطناعي" },
          ],
          color: "#B88500", accent: "rgba(184,133,0,0.08)", icon: "chart", metricVal: "<1ث", metricLabel: "بدلاً من أيام الانتظار", tag: "AI MIS Agent"
        },
        {
          num: "سير العمل 09", numShort: "09", title: "ذكاء إدارة الصيدلية والمخازن.", intro: "التنبؤ بالطلب، التنبيه لقرب انتهاء الصلاحية واقتراح البدائل بشكل تفاعلي.",
          rows: [
            { dt: getProblem("ar"), dd: "نقص المخزون يضر برعاية المرضى؛ تكدس المخزون يعطل السيولة والمالية." },
            { dt: getAi("ar"), dd: "حساب نقاط إعادة الطلب الذكية، التنبؤ بحجم الاستهلاك، وتنبيهات أمان المخزون." },
            { dt: getImpact("ar"), dd: "انخفاض 31% في نقص الأدوية · تقليل 18% في هدر الأدوية منتهية الصلاحية", impact: true },
            { dt: getModule("ar"), dd: "الصيدلية، المخازن" },
            { dt: getAiCap("ar"), dd: "Sahyogi AI · ClaimIQ" },
          ],
          color: "#CC2B58", accent: "rgba(204,43,88,0.08)", icon: "sparkle", metricVal: "31%", metricLabel: "انخفاض نقص المخزون", tag: "Sahyogi + ClaimIQ"
        },
        {
          num: "سير العمل 10", numShort: "10", title: "متابعة المرضى بعد الخروج.", intro: "إرسال تنبيهات تفاعلية ومتابعة التزام المرضى بالجرعات العلاجية بلغتهم.",
          rows: [
            { dt: getProblem("ar"), dd: "تدني معدلات التزام ومتابعة المرضى بعد الخروج من المستشفى لأقل من 30%." },
            { dt: getAi("ar"), dd: "يقوم وكيل Vaani AI بالتواصل وإرسال تنبيهات المتابعة بلغة المريض." },
            { dt: getImpact("ar"), dd: "+47% التزام بالمتابعة الطبية · نتائج صحية أفضل للمرضى", impact: true },
            { dt: getModule("ar"), dd: "تفاعل ومتابعة المرضى" },
            { dt: getAiCap("ar"), dd: "Vaani AI" },
          ],
          color: "#053C61", accent: "rgba(5,60,97,0.07)", icon: "chat", metricVal: "+47%", metricLabel: "ارتفاع نسبة المتابعة", tag: "Vaani AI"
        },
      ],
    };
  }

  // Fallback to English / Spanish / French (Using Spanish/French mappings mapped to English for simplicity or translated cleanly)
  // Let's make Spanish and French fall back to English or map them.
  const getFaqs = (lang: string) => {
    return [
      { q: "How is Chikitsa AI different from a chatbot?", a: "Every Chikitsa AI capability lives inside the workflow it improves, not as a bolted-on widget." },
      { q: "Which languages does Vaani AI support?", a: "Vaani supports 11 Indian languages on WhatsApp, web and voice." },
    ];
  };

  const heroEyebrowMap: Record<string, string> = {
    es: "IA en sus flujos",
    fr: "L'IA dans vos flux",
  };
  const heroTitleMap: Record<string, string> = {
    es: "IA que resuelve problemas hospitalarios reales.",
    fr: "Une IA qui résout de vrais problèmes hospitaliers.",
  };
  const heroDescMap: Record<string, string> = {
    es: "Chikitsa integra automatización con IA directamente en la facturación, documentación y consultorios.",
    fr: "Chikitsa intègre l'automatisation IA directement dans la facturation, l'admission et les consultations.",
  };
  const bookDemoMap: Record<string, string> = {
    es: "Reservar Demo",
    fr: "Réserver Démo",
  };
  const stepperLabelMap: Record<string, string> = {
    es: "Casos de Uso",
    fr: "Cas d'Usage",
  };

  return {
    heroEyebrow: heroEyebrowMap[locale] || "AI in your workflows",
    heroTitle: heroTitleMap[locale] || "AI that solves real hospital workflow problems.",
    heroDesc: heroDescMap[locale] || "Chikitsa brings AI into everyday hospital operations, from appointment booking and OPD documentation to claims, discharge summaries, analytics and patient engagement.",
    bookDemo: bookDemoMap[locale] || "Book a Demo",
    faqsEyebrow: "FAQs",
    faqsTitle: "AI workflow questions answered.",
    faqsDesc: "Five quick answers on how Chikitsa handles data privacy, models and clinical accuracy.",
    stepperLabel: stepperLabelMap[locale] || "Use Cases",
    moduleLabel: getModule(locale),
    aiEngineLabel: getAiCap(locale),
    faqs: getFaqs(locale),
    cases: [
      {
        num: "01", numShort: "01",
        title: locale === "es" ? "Reserva de citas e información." : locale === "fr" ? "Planification et support." : "Appointment booking & patient helpdesk.",
        intro: "WhatsApp, web and voice booking agent.",
        rows: [
          { dt: getProblem(locale), dd: "Phone lines busy and front desks overwhelmed." },
          { dt: getAi(locale), dd: "Sahyogi triages, books and reschedules across channels." },
          { dt: getImpact(locale), dd: "↓ 38% drop-off · 24×7 booking", impact: true },
          { dt: getModule(locale), dd: "Appointment, QMS" },
          { dt: getAiCap(locale), dd: "Sahyogi AI" },
        ],
        color: "#0A9E9A", accent: "rgba(10,158,154,0.08)", icon: "calendar", metricVal: "38%", metricLabel: "fewer drop-offs", tag: "Sahyogi AI"
      },
      {
        num: "02", numShort: "02",
        title: locale === "es" ? "Documentación por voz de OPD." : locale === "fr" ? "Documentation vocale OPD." : "OPD doctor desk documentation.",
        intro: "Doctors speak; Voice-MD writes structured SOAP notes directly into the EMR.",
        rows: [
          { dt: getProblem(locale), dd: "Doctors lose 40% of consult time typing into the EMR." },
          { dt: getAi(locale), dd: "Voice-MD captures, structures and codes the consult in real time." },
          { dt: getImpact(locale), dd: "+9 min back per patient · cleaner notes", impact: true },
          { dt: getModule(locale), dd: "Doctor Desk, EMR" },
          { dt: getAiCap(locale), dd: "Voice-MD" },
        ],
        color: "#2A9E5B", accent: "rgba(42,158,91,0.08)", icon: "micro", metricVal: "9 min", metricLabel: "saved", tag: "Voice-MD"
      },
      {
        num: "03", numShort: "03",
        title: locale === "es" ? "Digitalización de recetas." : locale === "fr" ? "Numérisation des prescriptions." : "Prescription digitisation.",
        intro: "Handwritten prescriptions auto-digitized.",
        rows: [
          { dt: getProblem(locale), dd: "Paper prescriptions break pharmacy and claims workflows." },
          { dt: getAi(locale), dd: "Lipi extracts formulary items with confidence checking." },
          { dt: getImpact(locale), dd: "96.4% drug accuracy · auto-routed", impact: true },
          { dt: getModule(locale), dd: "OPD, Pharmacy" },
          { dt: getAiCap(locale), dd: "Lipi AI" },
        ],
        color: "#B88500", accent: "rgba(184,133,0,0.08)", icon: "file", metricVal: "96.4%", metricLabel: "accuracy", tag: "Lipi AI"
      },
      {
        num: "04", numShort: "04",
        title: locale === "es" ? "Flujos de IPD y enfermería." : locale === "fr" ? "IPD & soins infirmiers." : "IPD & nursing workflows.",
        intro: "Nursing round logs captured by voice.",
        rows: [
          { dt: getProblem(locale), dd: "Nursing notes are inconsistent and rarely structured." },
          { dt: getAi(locale), dd: "Voice-MD structures notes, shift handovers & MAR." },
          { dt: getImpact(locale), dd: "2.4× faster handover · structured data", impact: true },
          { dt: getModule(locale), dd: "IPD, Nursing" },
          { dt: getAiCap(locale), dd: "Voice-MD" },
        ],
        color: "#CC2B58", accent: "rgba(204,43,88,0.08)", icon: "micro", metricVal: "2.4×", metricLabel: "faster handover", tag: "Voice-MD"
      },
      {
        num: "05", numShort: "05",
        title: locale === "es" ? "Redacción de informes de alta." : locale === "fr" ? "Génération des rapports de sortie." : "Discharge summary generation.",
        intro: "Discharge summaries compiled automatically.",
        rows: [
          { dt: getProblem(locale), dd: "Discharge summaries take hours and block bed turnaround." },
          { dt: getAi(locale), dd: "Auto-drafted summary from EMR, orders and nursing logs." },
          { dt: getImpact(locale), dd: "↓ 78% discharge time · bed turnaround", impact: true },
          { dt: getModule(locale), dd: "Discharge, IPD" },
          { dt: getAiCap(locale), dd: "Lipi AI" },
        ],
        color: "#053C61", accent: "rgba(5,60,97,0.07)", icon: "file", metricVal: "78%", metricLabel: "discharge time", tag: "Lipi AI"
      },
      {
        num: "06", numShort: "06",
        title: locale === "es" ? "Mapeo de ICD-10 y SNOMED." : locale === "fr" ? "Codage ICD-10 et SNOMED." : "ICD, LOINC & SNOMED coding assistance.",
        intro: "Clinical notes automatically mapped to international standards.",
        rows: [
          { dt: getProblem(locale), dd: "Coders are scarce and drive billing speed." },
          { dt: getAi(locale), dd: "Auto-coding to ICD-10, LOINC, SNOMED." },
          { dt: getImpact(locale), dd: "↓ 72% backlog · clean clinical BI", impact: true },
          { dt: getModule(locale), dd: "EMR, Laboratory" },
          { dt: getAiCap(locale), dd: "Coding AI" },
        ],
        color: "#0A9E9A", accent: "rgba(10,158,154,0.08)", icon: "chip", metricVal: "72%", metricLabel: "less backlog", tag: "Coding AI"
      },
      {
        num: "07", numShort: "07",
        title: locale === "es" ? "Soporte de reclamaciones NHCX." : locale === "fr" ? "Gestion des réclamations NHCX." : "NHCX / PMJAY claim workflow intelligence.",
        intro: "ClaimIQ validates documents, codes and tariffs before submission.",
        rows: [
          { dt: getProblem(locale), dd: "Rejection cycles eat 22% of claim revenue." },
          { dt: getAi(locale), dd: "Automated validation & rejection risk prediction." },
          { dt: getImpact(locale), dd: "97% first-pass approval · ↓ 11d cycle", impact: true },
          { dt: getModule(locale), dd: "Claims, Billing" },
          { dt: getAiCap(locale), dd: "ClaimIQ" },
        ],
        color: "#2A9E5B", accent: "rgba(42,158,91,0.08)", icon: "shield", metricVal: "97%", metricLabel: "first-pass", tag: "ClaimIQ"
      },
      {
        num: "08", numShort: "08",
        title: locale === "es" ? "Consultas en lenguaje natural." : locale === "fr" ? "Requêtes en langage naturel." : "MIS & BI analytics in plain English.",
        intro: "Ask hospital metrics, get instant chart answers.",
        rows: [
          { dt: getProblem(locale), dd: "Leaders wait days for simple operational metrics." },
          { dt: getAi(locale), dd: "AI MIS agent translates text questions to secure SQL." },
          { dt: getImpact(locale), dd: "Self-serve BI for executives in seconds", impact: true },
          { dt: getModule(locale), dd: "MIS Analytics" },
          { dt: getAiCap(locale), dd: "MIS Agent" },
        ],
        color: "#B88500", accent: "rgba(184,133,0,0.08)", icon: "chart", metricVal: "<1s", metricLabel: "wait time", tag: "MIS Agent"
      },
      {
        num: "09", numShort: "09",
        title: locale === "es" ? "Previsión de inventario." : locale === "fr" ? "Prévision des stocks." : "Pharmacy & inventory intelligence.",
        intro: "Expiry-risk alerts and automated drug reordering.",
        rows: [
          { dt: getProblem(locale), dd: "Stock-outs hurt care; excess stock expires." },
          { dt: getAi(locale), dd: "Demand forecasting & expiry safety levels." },
          { dt: getImpact(locale), dd: "↓ 31% stock-outs · ↓ 18% write-offs", impact: true },
          { dt: getModule(locale), dd: "Pharmacy, Inventory" },
          { dt: getAiCap(locale), dd: "Sahyogi + ClaimIQ" },
        ],
        color: "#CC2B58", accent: "rgba(204,43,88,0.08)", icon: "sparkle", metricVal: "31%", metricLabel: "fewer stockouts", tag: "Sahyogi + ClaimIQ"
      },
      {
        num: "10", numShort: "10",
        title: locale === "es" ? "Seguimiento automatizado." : locale === "fr" ? "Suivi automatisé des patients." : "Patient engagement & follow-up.",
        intro: "Follow-up reminders in native patient language.",
        rows: [
          { dt: getProblem(locale), dd: "Follow-up rate drops below 30% after discharge." },
          { dt: getAi(locale), dd: "Vaani AI nudges patients on WhatsApp in their language." },
          { dt: getImpact(locale), dd: "+47% follow-up rate · better NPS", impact: true },
          { dt: getModule(locale), dd: "Patient Engagement" },
          { dt: getAiCap(locale), dd: "Vaani AI" },
        ],
        color: "#053C61", accent: "rgba(5,60,97,0.07)", icon: "chat", metricVal: "+47%", metricLabel: "follow-up rate", tag: "Vaani AI"
      },
    ],
  };
};
